import {useCallback, useMemo} from 'react';
import {WhatYouWillLearn} from '@/features/course-landing-page/components/what-you-will-learn/what-you-will-learn.react-component';
import {RelatedTopics} from '@/features/course-landing-page/components/related-topics/related-topics.react-component';
import {Description} from '@/features/course-landing-page/components/course-description/course-description.react-component';
import {
    CourseIncludes,
    CourseIncludesPlacement,
} from '../course-includes/course-includes.react-component';
import {Requirements} from '@/features/course-landing-page/components/course-requirements/course-requirements.react-component';
import {useClpPurchaseContext} from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import {Curriculum} from '@/features/course-landing-page/components/course-curriculum/curriculum.react-component';
import {CodingExercisePromotion} from '@/features/course-landing-page/components/coding-exercise-promotion/coding-exercise-promotion.react-component';
import {InstructorList} from '@/features/course-landing-page/components/instructor-module/instructor-list/instructor-list.react-component';
import {ReviewsContainer} from '@/features/course-landing-page/components/reviews/reviews-container.react-component';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {useCourseCareerAccelerator} from '@/features/course-landing-page/context/career-accelerator-provider.react-component';
import {MoreCoursesByInstructors} from '@/features/course-landing-page/components/more-courses-by-instructors/more-courses-by-instructors.react-component';
import {FrequentlyBoughtTogetherWithBundleUnit} from '@/features/course-landing-page/components/bundle-unit/frequently-bought-together/frequently-bought-together.react-component';
import {StudentsAlsoBoughtRecommendationsWrapper} from '../students-also-bought/students-also-bought-recommendations-wrapper.react-component';
import {
    CertificationExamVoucherBanner,
    CourseCertificationUnit,
    GoogleCareerCourseCarousel,
    IncentiveTextType,
    useVoucherBannerData,
} from '@udemy-v2/react-certification-preparation';
import {publishEvent, TrackImpression} from '@udemy-v2/event-tracking';
import {ExperimentImpressionEvent} from '@udemy-v2/react-experimentation';
import {GrowWithGoogleCertificateBanner} from '@udemy-v2/react-subscription-browse';
import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {useCommonData} from '@udemy-v2/common-data';
import {getBundleFromCourse} from '@udemy-v2/react-bundle-unit';
import {useFeatures} from '@udemy-v2/react-experimentation';

import {CertificationBundle} from '../bundle-unit/certification-bundle/certification-bundle.react-component';
import {tokens} from '@udemy-v2/styles';
import styles from './course-body-content.module.scss';
import {ReportAbuse} from '../report-abuse/report-abuse.react-component';
import {LocalizedHtml, useI18n} from '@udemy-v2/i18n';
import {GoogleCareerAcceleratorData} from '@udemy-v2/react-certification-preparation';
import {getGoogleCareerPathUrl} from '@/features/course-landing-page/utils/google';

export const CourseBodyContent = () => {
    const commonData = useCommonData();
    const {gettext, interpolate} = useI18n();
    const hasOrganization = !!commonData?.organization;
    const isMobile = useMatchMedia(`(max-width: ${tokens['viewport-sm-max']})`);
    const course = useClpCourse();
    const {
        isLoading,
        isCourseInAvailablePlans,
        isPersonalPlanSubscriber,
        useCollapsiblePurchaseSection,
    } = useClpPurchaseContext();
    const courseCareerAccelerator = useCourseCareerAccelerator();
    const isMarketplace = !isPersonalPlanSubscriber && !hasOrganization;
    const courseIncludedInAvailableSubscription = !isLoading && isCourseInAvailablePlans;
    // Show 'Course includes' section in body for mobile users, if course is not included in subscription, or if the collapsible purchase section
    // (which does not render any incentives) is being used.
    const renderIncentivesInCourseBody =
        isMobile ||
        (!isMobile && courseIncludedInAvailableSubscription) ||
        useCollapsiblePurchaseSection;
    const bundles = getBundleFromCourse(Number(course.id));
    const courseIds = bundles?.contents.map((course) => course.id.toString()) || [];
    const hasCertBundle = bundles && courseIds.length > 0;
    const courseIncludesCodingExercise =
        course.curriculum?.contentCounts?.codingExercisesCount &&
        course.curriculum.contentCounts.codingExercisesCount > 0;
    const showUpdatedCertificationUnit = !hasOrganization;
    const {isLoading: isFeaturesLoading, featureVariantAssignments} = useFeatures([
        'show_exam_voucher_banner_in_clp',
    ]);

    const isGrowWithGoogleCourse = !!course.contentCollectionInfo?.isInGooglePartnerCollection;

    const examVoucherFeatureAssignment = featureVariantAssignments.get(
        'show_exam_voucher_banner_in_clp',
    );
    const showExamVoucherBannerInCLP =
        examVoucherFeatureAssignment?.configuration?.showExamVoucherBannerInCLP;

    // Build ordered list of topic IDs: primary topic first, then related topics as fallbacks.
    // This handles courses where the primary topic is an aggregate (e.g. "Amazon AWS") with no
    // exam, while the specific cert topic (e.g. "AWS Certified Solutions Architect") lives in
    // the related topics array.
    const voucherTopicIds: string[] = [];
    if (course?.primaryTopic?.id != null) voucherTopicIds.push(String(course.primaryTopic.id));
    course?.topics?.forEach((topic) => {
        const id = String(topic.id);
        if (!voucherTopicIds.includes(id)) voucherTopicIds.push(id);
    });

    const {canRender: canRenderVoucherBanner, bannerData} = useVoucherBannerData(voucherTopicIds);
    const handleExperimentImpression = useCallback(() => {
        publishEvent(
            new ExperimentImpressionEvent({
                experimentIds: examVoucherFeatureAssignment?.experimentIds ?? [],
                isLocalDefaultShown: false,
            }),
        );
    }, [examVoucherFeatureAssignment]);

    const bundleContextInput = useMemo(
        () => ({
            contextId: 'clp-bundle' as const,
            sourceObjects: [{id: course.id, type: 'COURSE' as never}],
        }),
        [course.id],
    );

    const pathName = courseCareerAccelerator?.career?.name ?? '';

    const certificateBannerDescription = courseCareerAccelerator ? (
        <LocalizedHtml
            html={interpolate(
                gettext(
                    'After completion of all courses in the <a class="certificate">%(certificateName)s</a> path, earn a professional certificate that you can share on social media, LinkedIn, resume, or CV. ',
                ),
                {certificateName: pathName},
                true,
            )}
            interpolate={{
                certificate: (
                    <a href={getGoogleCareerPathUrl(courseCareerAccelerator.slug)}>{pathName}</a>
                ),
            }}
        />
    ) : null;

    return (
        <div className={styles.container}>
            {courseCareerAccelerator &&
                courseCareerAccelerator.certificateInfo?.certificateImageUrl && (
                    <GrowWithGoogleCertificateBanner
                        iconButtonProps={{
                            href: getGoogleCareerPathUrl(courseCareerAccelerator.slug),
                        }}
                        description={certificateBannerDescription}
                        title={gettext('Earn a certificate from Google')}
                    />
                )}

            <div
                className={
                    showExamVoucherBannerInCLP
                        ? styles['certification-banner-placement-container']
                        : styles['certification-course-unit-placement-container']
                }
            >
                <WhatYouWillLearn objectives={course.learningOutcomes} />
                {!isFeaturesLoading && canRenderVoucherBanner && (
                    <TrackImpression trackFunc={handleExperimentImpression} onlyOnce={true}>
                        <div />
                    </TrackImpression>
                )}
                {!isFeaturesLoading &&
                    (showExamVoucherBannerInCLP ? (
                        canRenderVoucherBanner &&
                        bannerData && <CertificationExamVoucherBanner bannerData={bannerData} />
                    ) : (
                        <CourseCertificationUnit
                            courseId={Number(course.id)}
                            linkToTopic={isMarketplace}
                            showLearnMoreModal={isMarketplace}
                            isMobile={isMobile}
                            showAddToInterestButton={showUpdatedCertificationUnit}
                            showIncentiveTextType={IncentiveTextType.PUBLIC}
                        />
                    ))}
            </div>

            <RelatedTopics course={course} />

            {renderIncentivesInCourseBody && (
                <CourseIncludes placement={CourseIncludesPlacement.BODY} />
            )}

            {!!courseIncludesCodingExercise && (
                <CodingExercisePromotion
                    courseId={Number(course.id)}
                    frontendTrackingId={course.frontendTrackingId || ''}
                />
            )}

            <Curriculum />

            {!course.isPracticeTestCourse && <Requirements prerequisites={course.prerequisites} />}

            <Description description={course.description} targetAudience={course.targetAudience} />

            {isGrowWithGoogleCourse && courseCareerAccelerator && (
                <GoogleCareerCourseCarousel
                    ariaLabel={gettext('Google Career Certificate courses')}
                    acceleratorData={[courseCareerAccelerator] as GoogleCareerAcceleratorData[]}
                    ctaHref={getGoogleCareerPathUrl(courseCareerAccelerator.slug)}
                />
            )}

            <StudentsAlsoBoughtRecommendationsWrapper />

            <div className={styles['bundle-unit-container']}>
                {hasCertBundle ? (
                    <CertificationBundle bundleCourseIds={courseIds} />
                ) : hasOrganization ? null : (
                    <FrequentlyBoughtTogetherWithBundleUnit contextInput={bundleContextInput} />
                )}
            </div>

            <InstructorList />

            <ReviewsContainer courseId={parseInt(course.id, 10)} />

            {!hasOrganization && <MoreCoursesByInstructors instructors={course.instructors} />}

            <ReportAbuse
                objectId={Number(course.id)}
                objectType="course"
                fullWidthButton
                udStyle="secondary"
                withDivider
            />
        </div>
    );
};
